import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../../../../assets/images/brand/logo-black.png';
import LogoWhite from '../../../../../assets/images/brand/logo-white-color.svg';
import LogoIcon from '../../../../../assets/images/brand/only-logo.png';
import { useUserStore } from '../../../../../modules/Auth/stores/authStore';
import {
  getUserPermissions,
  hasExactPermission,
} from '../../../../accessControl/utils';
import { Button } from '../../../../components/atoms/Button';
import { Chip } from '../../../../components/atoms/Chip';
import DropDown from '../../../../components/molecules/Dropdown';
import { Typography } from '../../../../components/molecules/Typography';
import NotificationCard from '../../../../components/organism/NotificationCard';
import RedirectModel from '../../../../components/organism/RedirectModal';
import { notificationsHook } from '../../../../hooks/notificationsHook';
import { useIconFontReady } from '../../../../hooks/useIconFontReady';
import { useMenuHooks } from '../../../../hooks/useMenuHooks';
import { useNotificationsStore } from '../../../../stores/useNotifications';
import { showChipAprovaLive } from '../../../../utils/aprovaLive';
import SearchInput from '../searchInput';
import { MenuBarIconsSkeleton, MenuBarSkeleton } from './MenuBarSkeleton';
import {
  IMenuItem,
  ISideMenuItem,
  menuItems,
  sideMenuItems,
} from './menuItems';
import './styles.css';
import { row } from './variants';
import { Permissions } from '../../../../accessControl/permissions';
import { isPlanoAdaptativo as fnIsPlanoAdaptativo } from '../../../../utils/menu';

const MenuBar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { callBack, isChecked } = useMenuHooks();
  const { mutate } = notificationsHook({ page: 1 });
  const { hasNotificationPendindg, countNotificationPendindg } =
    useNotificationsStore();

  const [isMenuVisible, setMenuVisible] = useState(true);
  const [redirectURL] = useState<string>('');
  const [openRedirect, setOpenRedirect] = useState<boolean>(false);
  const [redirectModule] = useState<string>('');
  const [isMobileSearchOpen, setMobileSearchOpen] = useState<boolean>(false);
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);

  const { user: meData } = useUserStore();
  const isIconFontReady = useIconFontReady();

  const setActive = (url: string) => {
    if (pathname?.includes(url)) {
      return 'selected';
    } else {
      return 'focusable';
    }
  };

  const setActiveItem = (item: IMenuItem) => {
    if (pathname === '/') {
      return item.href === '/' ? 'selected' : 'hoverable';
    }

    // Items with a dropdown are matched against their own sub-item links,
    // since sibling top-level items (e.g. Disciplinas/Universitário) can share the same href.
    const candidateUrls =
      item.items && item.items.length > 0
        ? item.items.map((subItem) => subItem.link)
        : [item.href || ''];

    const isSelected = candidateUrls.some(
      (url) => url && url !== '/' && pathname?.includes(url),
    );

    return isSelected ? 'selected' : 'hoverable';
  };

  useEffect(() => {
    mutate({ page: 1, limit: 15 });
  }, [mutate]);

  useEffect(() => {
    if (window.screen.width < 901) {
      const handleScroll = () => {
        if (window.scrollY > 100) {
          setMenuVisible(false);
        } else {
          setMenuVisible(true);
        }
      };

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const bgColorDropDown = meData?.isDarkMode
    ? { backgroundColor: '#000C29' }
    : { backgroundColor: '#ffffff' };
  //@todo: ver como utilizar os design tokens

  const filteredMenuItems = useMemo(() => {
    const userPermission = getUserPermissions(meData);
    const menuItemsFiltered: IMenuItem[] = [];

    menuItems.forEach((menuItem) => {
      if (
        menuItem.permission &&
        hasExactPermission(userPermission, menuItem.permission) &&
        menuItem.items.length <= 0
      ) {
        menuItemsFiltered.push(menuItem);
        return;
      }

      const filteredItems = menuItem.items?.filter(
        (item) =>
          hasExactPermission(userPermission, item.permission) ||
          !item.permission
      );

      if (filteredItems && filteredItems.length > 0) {
        menuItemsFiltered.push({ ...menuItem, items: filteredItems });
      }
    });

    if (!meData || (meData?.permissions?.modulesGranted || []).includes('aprova-live')) {
      const index = menuItemsFiltered.findIndex(
        (item) => item.label === 'Ao Vivo'
      );

      if (index !== -1) {
        const itemIndex = menuItemsFiltered[index].items.findIndex(
          (item) => item.permission === Permissions.AO_VIVO_CHALLENGE_60_DIAS
        );
        if (itemIndex !== -1) {
          menuItemsFiltered[index].items.splice(itemIndex, 1);
        }
      }
    }

    return menuItemsFiltered;
  }, [meData]);

  const filteredSideMenuItems = useMemo(() => {
    const userPermission = getUserPermissions(meData);
    const sideMenuItemsFiltered: ISideMenuItem[] = [];

    sideMenuItems.forEach((sideMenuItem) => {
      if (!sideMenuItem.permission && sideMenuItem.items.length <= 0) {
        sideMenuItemsFiltered.push(sideMenuItem);
        return;
      }

      if (
        sideMenuItem.permission &&
        sideMenuItem.items.length <= 0 &&
        hasExactPermission(userPermission, sideMenuItem.permission)
      ) {
        sideMenuItemsFiltered.push(sideMenuItem);
      }

      const filteredItems = sideMenuItem.items?.filter(
        (item) =>
          !item.permission ||
          hasExactPermission(userPermission, item.permission)
      );

      if (filteredItems && filteredItems.length > 0) {
        sideMenuItemsFiltered.push({ ...sideMenuItem, items: filteredItems });
      }
    });

    // if (
    //   !meData ||
    //   !meData?.permissions.modulesGranted.includes('desafio-60-dias-2025')
    // ) {
    //   const index = sideMenuItemsFiltered[0].items.findIndex(
    //     (item) => item.permission === Permissions.AO_VIVO_CHALLENGE_60_DIAS
    //   );
    //   sideMenuItemsFiltered[0].items.splice(index, 1);
    // }

    return sideMenuItemsFiltered;
  }, [meData]);

  const isPlanoAdaptativo = fnIsPlanoAdaptativo();

  const isMenuLoading = !meData || !isIconFontReady;

  return (
    <div className={row({ visibility: isMenuVisible ? 'show' : 'hide' })}>
      <RedirectModel
        onCheck={() => callBack(redirectModule)}
        checked={isChecked(redirectModule)}
        onClose={() => setOpenRedirect(false)}
        isOpen={openRedirect}
        redirect={redirectURL}
      />
      <div className='flex h-full w-fit flex-1 flex-row items-center justify-start p-0'>
        <Link to='/' className='xs:hidden xl:block'>
          <img
            src={meData?.isDarkMode ? LogoWhite : Logo}
            alt='Logo'
            width='138.09px'
            height='24px'
          />
        </Link>
        <Link to='/' className='xs:hidden lg:block xl:hidden'>
          <img src={LogoIcon} alt='Logo' width='16.6px' height='24px' />
        </Link>
        <div className='xs:block lg:hidden'>
          {isMenuLoading && <MenuBarIconsSkeleton count={1} size={32} />}
          {!isMenuLoading && isPlanoAdaptativo && (
            <Button
              allowReadOnly
              iconVariant='rounded'
              size='small'
              variant='hoverable'
              LeftIcon={'arrow_back'}
              iconType='material'
              onlyIcon
              data-tooltip-id={'menu-tooltip-back'}
              onClick={() => navigate(-1)}
            />
          )}
          {!isMenuLoading && !isPlanoAdaptativo && (
            <>
              <Button
              allowReadOnly
                iconVariant='rounded'
                size='small'
                variant='hoverable'
                LeftIcon={'search'}
                iconType='material'
                onlyIcon
                data-tooltip-id={'menu-tooltip-search'}
              />
              <DropDown
                id={`menu-tooltip-search`}
                offset={20}
                position='bottom-end'
                style={bgColorDropDown}
                isOpen={isMobileSearchOpen}
                setIsOpen={setMobileSearchOpen}
                openEvents={{ click: true }}
                closeEvents={{}}
                globalCloseEvents={{ clickOutsideAnchor: true, escape: true }}
              >
                <SearchInput onSearchSubmit={() => setMobileSearchOpen(false)} />
              </DropDown>
            </>
          )}
        </div>
      </div>
      <div className='flex-2 w-fit'>
        <div className='m-auto h-full w-fit flex-row items-center xs:hidden lg:flex lg:gap-xs xl:gap-lg'>
          {isMenuLoading && (
            <MenuBarSkeleton items={filteredMenuItems.length || 6} />
          )}
          {!isMenuLoading &&
            filteredMenuItems.map((it, i) => {
            if (!meData?.enableNewFeatures26 && it.label === 'Flashcards') {
              return null;
            }

            return (
              <Fragment key={i}>
                <Button
              allowReadOnly
                  size='regular'
                  variant={setActiveItem(it)}
                  LeftIcon={it.icon}
                  iconType={it.iconType}
                  data-tooltip-id={`top-menu-${i}`}
                  asLink={it.clickable}
                  to={it.clickable ? it.href : ''}
                  iconVariant='rounded'
                  className='relative'
                >
                  {it.label === 'Minha Área' ? (
                    <Typography className='hidden xl:block'>
                      {it.label}
                    </Typography>
                  ) : (
                    it.label
                  )}
                  {it.label === 'Ao Vivo' && showChipAprovaLive(meData) && (
                    <div className='relative ml-1 h-[16px] w-[16px]'>
                      <div className='bg-secondary-500 absolute h-[16px] w-[16px] animate-ping rounded-circular opacity-75'></div>
                      <div className='bg-secondary-500 absolute left-[1px] top-[1px] h-[14px] w-[14px] rounded-circular '></div>
                    </div>
                  )}
                </Button>
                {it.items && it.items.length > 0 && (
                  <DropDown
                    id={`top-menu-${i}`}
                    offset={20}
                    position='bottom-end'
                    style={bgColorDropDown}
                  >
                    <div
                      id='dropContentMenu'
                      className='max-h-[70vh] overflow-y-auto'
                    >
                      {it.items.map((menuItems, i) => (
                        <Button
              allowReadOnly
                          iconVariant='rounded'
                          variant={setActive(menuItems.link)}
                          key={i}
                          LeftIcon={menuItems.icon}
                          iconType={menuItems.iconType}
                          iconSize={menuItems.iconSize}
                          className='relative w-full text-left'
                          asLink
                          to={menuItems.link}
                        >
                          {/*{menuItems.isNew && (*/}
                          {/*  <div className='absolute left-2 top-1/2 -translate-y-1/2 bg-feedback-color-error-pure rounded-pill px-1 py-0.5 -rotate-45'>*/}
                          {/*    <Typography size="mini" className='text-mono-color-white'>*/}
                          {/*      Novo*/}
                          {/*    </Typography>*/}
                          {/*  </div>*/}
                          {/*)}*/}
                          {menuItems.label}
                          {menuItems.label === 'AprovaLive' &&
                            showChipAprovaLive(meData) && (
                              <Chip variant='filled' radius='pill' size='sm'>
                                NOVO!
                              </Chip>
                            )}
                        </Button>
                      ))}
                    </div>
                  </DropDown>
                )}
              </Fragment>
            );
          })}
        </div>
        <div className='m-auto h-full w-fit flex-row items-center xs:flex lg:hidden'>
          <Link to='/'>
            <img
              src={meData?.isDarkMode ? LogoWhite : Logo}
              alt='Logo black'
              width='153.57px'
              height='18px'
            />
          </Link>
        </div>
      </div>
      <div className='w-fit flex-1'>
        <>
          <div className='flex h-full w-full flex-row items-center justify-end gap-y-lg p-0'>
            {isMenuLoading && (
              <div className='flex justify-end'>
                <MenuBarIconsSkeleton count={3} />
              </div>
            )}
            {!isMenuLoading && (
              <>
            <Button
              allowReadOnly
              iconVariant='rounded'
              size='regular'
              variant='hoverable'
              LeftIcon='search'
              data-tooltip-id={`side-menu-search`}
              className={`${isPlanoAdaptativo ? 'flex' : 'hidden lg:flex'}`}
            />
            <DropDown
              id={`side-menu-search`}
              offset={10}
              position='bottom-end'
              style={bgColorDropDown}
              isOpen={isSearchOpen}
              setIsOpen={setSearchOpen}
              openEvents={{ click: true }}
              closeEvents={{}}
              globalCloseEvents={{ clickOutsideAnchor: true, escape: true }}
            >
              <SearchInput onSearchSubmit={() => setSearchOpen(false)} />
            </DropDown>
            <Button
              allowReadOnly
              iconVariant='rounded'
              size='regular'
              variant='hoverable'
              LeftIcon='notifications_none'
              data-tooltip-id={`side-menu-news`}
              className={`${isPlanoAdaptativo ? 'hidden lg:flex' : 'relative'}`}
            >
              {hasNotificationPendindg && (
                <span
                  className='absolute right-4 top-2 inline-flex -translate-y-1/2 translate-x-1/2 transform items-center
              justify-center rounded-circular bg-feedback-color-error-pure px-1.5 py-1 text-xs
               font-bold leading-none text-mono-color-white'
                >
                  {countNotificationPendindg}
                </span>
              )}
            </Button>
            <DropDown
              id={`side-menu-news`}
              offset={20}
              position='bottom-end'
              className='max-h-[calc(100vh-250px)] max-w-[95vw] overflow-auto bg-mono-color-darkest p-3 lg:max-h-[470px]'
            >
              <NotificationCard />
            </DropDown>

            <span className={'hidden lg:flex'}>
              {filteredSideMenuItems.map((it, i) => {
                if (it.icon === 'menu') {
                  return (
                    <Fragment key={i}>
                      <Button
              allowReadOnly
                        iconVariant='rounded'
                        size='regular'
                        variant='hoverable'
                        LeftIcon={it.icon}
                        data-tooltip-id={`side-menu-${i}`}
                      />
                      {it.items.length > 0 && (
                        <DropDown
                          id={`side-menu-${i}`}
                          offset={20}
                          position='bottom-end'
                          style={bgColorDropDown}
                        >
                          <div className='bg-color-lightest'>
                            {it.items.map((item: any, i) => {
                              return (
                                <Button
              allowReadOnly
                                  iconVariant='rounded'
                                  variant={setActive(item.link)}
                                  key={i}
                                  LeftIcon={item.icon}
                                  iconType={item.iconType}
                                  iconSize={item.iconSize}
                                  className='w-full text-left'
                                  target={item.externalLink ? '_blank' : ''}
                                  asLink
                                  to={item.link}
                                >
                                  {item.label}{' '}
                                  {item.isNew && (
                                    <div className='rounded-sm bg-brand-color-secondary-pure px-1 py-0.5 text-xs text-mono-color-white'>
                                      Novo
                                    </div>
                                  )}
                                </Button>
                              );
                            })}
                          </div>
                        </DropDown>
                      )}
                    </Fragment>
                  );
                }
              })}
            </span>
              </>
            )}
          </div>
        </>
      </div>
    </div>
  );
};

export default MenuBar;
